import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, useToast } from "@ir/ui";
import {
  CreateMasterDataProductDTO,
  GetMasterDataProductCategoriesDTO,
  GetMasterDataProductUomsDTO,
  UpdateMasterDataProductDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataProduct } from "@src/domain/entities/masterData";
import {
  CreateMasterDataProductUseCase,
  GetMasterDataProductCategoriesUseCase,
  GetMasterDataProductUomsUseCase,
  UpdateMasterDataProductUseCase,
} from "@src/application/useCases/masterData";
import {
  MasterDataProductCategoryRepository,
  MasterDataProductRepository,
  MasterDataProductUomRepository,
} from "@src/infrastructure/repositories/masterData";
import { useMasterDataProducts } from "@src/presentation/providers/MasterDataProductsDataProvider";
import { invalidateMasterDataListCacheScopes } from "@src/presentation/providers/masterData/masterDataListCache";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataProductAddEditForm,
  PRODUCT_CATEGORY_REQUIRED_MESSAGE,
  PRODUCT_UOM_REQUIRED_MESSAGE,
  type ProductAddEditFormValues,
  type ProductCategorySelectOption,
  type ProductUomSelectOption,
} from "../utils/MasterDataProductAddEditForm";
import { MasterDataProductsTable } from "../utils/MasterDataProductsTable";
import { MasterDataProductPriceHistoryModal } from "../utils/MasterDataProductPriceHistoryModal";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import {
  isProductKeyDuplicateError,
  PRODUCT_KEY_ALREADY_EXISTS_MESSAGE,
} from "../utils/productKeyValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_FORM_VALUES: ProductAddEditFormValues = {
  itemName: "",
  productKey: "",
  categoryId: null,
  uomId: null,
  itemDescription: "",
  upcItemNumber: "",
  glItemNumber: "",
  glItemDescription: "",
  isActive: true,
};

export const MasterDataProductsPage = () => {
  const { products, loading, search, setSearch, total, setPage, refetch } =
    useMasterDataProducts();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<ProductAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingProduct,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataProduct>();

  const [categoryOptions, setCategoryOptions] = useState<
    ProductCategorySelectOption[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [uomOptions, setUomOptions] = useState<ProductUomSelectOption[]>([]);
  const [uomsLoading, setUomsLoading] = useState(false);
  const [priceProduct, setPriceProduct] = useState<MasterDataProduct | null>(
    null,
  );
  const categoriesLoadedRef = useRef(false);
  const uomsLoadedRef = useRef(false);

  const productRepository = useMemo(() => new MasterDataProductRepository(), []);
  const categoryRepository = useMemo(
    () => new MasterDataProductCategoryRepository(),
    [],
  );
  const uomRepository = useMemo(() => new MasterDataProductUomRepository(), []);
  const createUseCase = useMemo(
    () => new CreateMasterDataProductUseCase(productRepository),
    [productRepository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataProductUseCase(productRepository),
    [productRepository],
  );
  const getCategoriesUseCase = useMemo(
    () => new GetMasterDataProductCategoriesUseCase(categoryRepository),
    [categoryRepository],
  );
  const getUomsUseCase = useMemo(
    () => new GetMasterDataProductUomsUseCase(uomRepository),
    [uomRepository],
  );

  const initialValues = useMemo((): ProductAddEditFormValues => {
    if (modalMode === "edit" && editingProduct) {
      return {
        itemName: editingProduct.itemName,
        productKey: editingProduct.productKey,
        categoryId: editingProduct.categoryId,
        uomId: editingProduct.uomId,
        itemDescription: editingProduct.itemDescription ?? "",
        upcItemNumber: editingProduct.upcItemNumber ?? "",
        glItemNumber: editingProduct.glItemNumber ?? "",
        glItemDescription: editingProduct.glItemDescription ?? "",
        isActive: editingProduct.isActive,
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingProduct, modalMode]);

  const mergeEditingCategory = useCallback(
    (
      options: ProductCategorySelectOption[],
      product: MasterDataProduct | null,
    ): ProductCategorySelectOption[] => {
      if (product?.categoryId == null || !product.categoryName) {
        return options;
      }
      if (
        options.some(
          (option) => option.productCategoryId === product.categoryId,
        )
      ) {
        return options;
      }
      return [
        {
          productCategoryId: product.categoryId,
          name: product.categoryName,
          categoryKey: product.categoryKey ?? "",
        },
        ...options,
      ];
    },
    [],
  );

  const mergeEditingUom = useCallback(
    (
      options: ProductUomSelectOption[],
      product: MasterDataProduct | null,
    ): ProductUomSelectOption[] => {
      if (product?.uomId == null || !product.uomName) {
        return options;
      }
      if (options.some((option) => option.uomId === product.uomId)) {
        return options;
      }
      return [
        {
          uomId: product.uomId,
          uomName: product.uomName,
          uomCode: product.uomCode ?? "",
        },
        ...options,
      ];
    },
    [],
  );

  const ensureCategoriesForModal = useCallback(async () => {
    // Reuse cached categories for the Products page session; only merge
    // a missing edit-category locally (e.g. inactive) without refetching.
    if (categoriesLoadedRef.current) {
      setCategoryOptions((prev) => mergeEditingCategory(prev, editingProduct));
      return;
    }

    setCategoriesLoading(true);
    try {
      const result = await getCategoriesUseCase.execute(
        new GetMasterDataProductCategoriesDTO({
          search: null,
          includeInactive: false,
        }),
      );

      const options: ProductCategorySelectOption[] = result.items.map(
        (category) => ({
          productCategoryId: category.productCategoryId,
          name: category.name,
          categoryKey: category.categoryKey,
        }),
      );

      setCategoryOptions(mergeEditingCategory(options, editingProduct));
      categoriesLoadedRef.current = true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load categories";
      errorToast({ content: message });
      setCategoryOptions([]);
      // Leave categoriesLoadedRef false so the next modal open can retry.
    } finally {
      setCategoriesLoading(false);
    }
  }, [editingProduct, errorToast, getCategoriesUseCase, mergeEditingCategory]);

  const ensureUomsForModal = useCallback(async () => {
    if (uomsLoadedRef.current) {
      setUomOptions((prev) => mergeEditingUom(prev, editingProduct));
      return;
    }

    setUomsLoading(true);
    try {
      const result = await getUomsUseCase.execute(
        new GetMasterDataProductUomsDTO({ search: null }),
      );

      const options: ProductUomSelectOption[] = result.items.map((uom) => ({
        uomId: uom.uomId,
        uomName: uom.uomName,
        uomCode: uom.uomCode,
      }));

      setUomOptions(mergeEditingUom(options, editingProduct));
      uomsLoadedRef.current = true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load UOMs";
      errorToast({ content: message });
      setUomOptions([]);
    } finally {
      setUomsLoading(false);
    }
  }, [editingProduct, errorToast, getUomsUseCase, mergeEditingUom]);

  useEffect(() => {
    if (modalMode == null) return;
    void ensureCategoriesForModal();
    void ensureUomsForModal();
  }, [ensureCategoriesForModal, ensureUomsForModal, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: ProductAddEditFormValues) => {
      setIsSaving(true);
      try {
        const itemName = values.itemName.trim();
        const productKey = values.productKey.trim();
        const itemDescription = values.itemDescription.trim();
        const upcItemNumber = values.upcItemNumber.trim();
        const glItemNumber = values.glItemNumber?.trim() || null;
        const glItemDescription = values.glItemDescription?.trim() || null;
        const categoryId = Number(values.categoryId);
        const uomId = Number(values.uomId);
        const isActive = Boolean(values.isActive);

        if (!Number.isFinite(categoryId) || categoryId <= 0) {
          form.setFields([
            { name: "categoryId", errors: [PRODUCT_CATEGORY_REQUIRED_MESSAGE] },
          ]);
          setIsFormSubmittable(false);
          return;
        }

        if (!Number.isFinite(uomId) || uomId <= 0) {
          form.setFields([
            { name: "uomId", errors: [PRODUCT_UOM_REQUIRED_MESSAGE] },
          ]);
          setIsFormSubmittable(false);
          return;
        }

        if (modalMode === "edit" && editingProduct) {
          await updateUseCase.execute(
            new UpdateMasterDataProductDTO({
              productId: editingProduct.productId,
              itemName,
              productKey,
              categoryId,
              uomId,
              itemDescription,
              upcItemNumber,
              glItemNumber,
              glItemDescription,
              isActive,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataProductDTO({
              itemName,
              productKey,
              categoryId,
              uomId,
              itemDescription,
              upcItemNumber,
              glItemNumber,
              glItemDescription,
              isActive,
            }),
          );
        }

        successToast({
          content: "Product saved successfully",
        });
        closeModal();
        invalidateMasterDataListCacheScopes(["product-uoms"]);
        refetch();
      } catch (err) {
        if (isProductKeyDuplicateError(err)) {
          form.setFields([
            {
              name: "productKey",
              errors: [PRODUCT_KEY_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: "Failed to save product. Please try again.",
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingProduct,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  return (
    <>
      <MasterDataWritableListLayout
        title="Products"
        subtitle={formatMasterDataRecordCount(loading, total)}
        searchPlaceholder="Search products"
        search={search}
        setSearch={setSearch}
        searchSection="products"
        addButtonLabel="Add Product"
        onAdd={openCreateModal}
        loading={loading}
      >
        <MasterDataProductsTable
          products={products}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
          onViewPrices={setPriceProduct}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Product"
        editTitle="Edit Product"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataProductAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
          uomOptions={uomOptions}
          uomsLoading={uomsLoading}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataProductPriceHistoryModal
        product={priceProduct}
        isOpen={priceProduct != null}
        onClose={() => setPriceProduct(null)}
      />
    </>
  );
};
